/**
 * Custom question bank API service for AskAsper
 * CRUD operations for user-defined custom questions.
 */

import { ASK_ASPER_BASE_URL } from '../config';

export interface CustomQuestionBankResponse {
    QuestionID: number;
    Category: string;
    Domain: string;
    Question: string;
    user_email: string;
}

export interface CustomQuestionBankCreateRequest {
    QuestionID?: number | string;
    UserID: string;
    CustomerID: string;
    Question: string;
}

export interface CustomQuestionBankUpdateRequest {
    QuestionID: number;
    UserID: string;
    CustomerID: string;
    Question: string;
}

export interface CustomQuestionBankDeleteRequest {
    QuestionID: number;
    UserID: string;
}

function getAuthHeader(): string {
    const token = localStorage.getItem('accessToken');
    return `Bearer ${token}`;
}

/**
 * Get all custom questions for a specific user
 */
export async function getCustomQuestions(userId: string): Promise<CustomQuestionBankResponse[]> {
    const response = await fetch(
        `${ASK_ASPER_BASE_URL}custom-question-bank?user_id=${encodeURIComponent(userId)}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: getAuthHeader(),
            },
        },
    );

    if (!response.ok) {
        if (response.status === 204) {
            return [];
        }
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return data || [];
    }

    return [];
}

/**
 * Create a new custom question for a user
 */
export async function createCustomQuestion(
    request: CustomQuestionBankCreateRequest,
): Promise<CustomQuestionBankResponse> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}custom-question-bank`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
}

/**
 * Update an existing custom question
 */
export async function updateCustomQuestion(
    request: CustomQuestionBankUpdateRequest,
): Promise<CustomQuestionBankResponse> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}custom-question-bank`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Custom question not found');
        }
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data;
}

/**
 * Delete a custom question for a user
 */
export async function deleteCustomQuestion(
    request: CustomQuestionBankDeleteRequest,
): Promise<void> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}custom-question-bank`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Custom question not found');
        }
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
}
